import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Eye, Filter, ArrowUpDown, Loader2, AlertCircle } from 'lucide-react';

interface ServiceRequest {
  id: string;
  service_category: string;
  service_type: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  status: 'pending' | 'quoted' | 'accepted' | 'rejected';
  quoted_price?: number;
  provider_id?: number;
  payment_status?: string;
  created_at: string;
  property_type: string;
}

const AdminServiceRequestsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  useEffect(() => {
    fetchServiceRequests();
  }, [filters, sortBy, sortOrder]);

  const fetchServiceRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = '/admin/service-requests?';
      const params = new URLSearchParams();

      if (filters.status) {
        params.append('status', filters.status);
      }
      params.append('sort', sortBy);
      params.append('order', sortOrder);
      params.append('limit', '100');

      const response = await apiService.request(query + params.toString(), 'GET');

      if (response.success) {
        const data = Array.isArray(response.data) ? response.data : [];
        // Filter by search term if provided
        const filtered = data.filter(req =>
          !filters.search ||
          req.contact_name?.toLowerCase().includes(filters.search.toLowerCase()) ||
          req.contact_email?.toLowerCase().includes(filters.search.toLowerCase()) ||
          req.id?.toLowerCase().includes(filters.search.toLowerCase())
        );
        setRequests(filtered);
      } else {
        setError(response.message || 'Failed to fetch service requests');
      }
    } catch (err: any) {
      console.error('Error fetching service requests:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'quoted':
        return 'bg-blue-100 text-blue-800';
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusBadgeColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Service Requests - Admin | Virewire</title>
      </Helmet>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Requests</h1>
            <p className="text-gray-600 mt-1">Manage customer service requests and assignments</p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-blue-600">{requests.length}</div>
            <p className="text-sm text-gray-600">Total Requests</p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="border-l-4 border-l-red-500 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-900 font-semibold">Error</p>
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters & Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Search by name, email, or ID..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="quoted">Quoted</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="created_at">Date Created</option>
                    <option value="quoted_price">Price</option>
                    <option value="status">Status</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Requests Table */}
        <Card>
          <CardContent className="pt-6">
            {requests.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No service requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Request ID</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Customer</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Service</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Status</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Quote Price</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Payment</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Date</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {requests.map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-sm font-medium text-gray-900">
                          {request.id}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{request.contact_name}</p>
                            <p className="text-sm text-gray-500">{request.contact_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">
                              {request.service_type?.replace('-', ' ')}
                            </p>
                            <p className="text-sm text-gray-500 capitalize">
                              {request.service_category?.replace('-', ' ')}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge className={getStatusBadgeColor(request.status)}>
                            {request.status?.charAt(0).toUpperCase() + request.status?.slice(1)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          {request.quoted_price ? (
                            <p className="font-semibold text-gray-900">
                              £{(request.quoted_price * 1.2).toFixed(2)}
                            </p>
                          ) : (
                            <span className="text-gray-500">Not quoted</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {request.payment_status && (
                            <Badge className={getPaymentStatusBadgeColor(request.payment_status)}>
                              {request.payment_status?.charAt(0).toUpperCase() + request.payment_status?.slice(1)}
                            </Badge>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(request.created_at).toLocaleDateString('en-GB')}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/service-requests/${request.id}`)}
                            className="gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminServiceRequestsPage;
